/**
 * Signal Seeder
 * ---------------------------------------------------------------
 * Tactical operator console that injects synthetic — but
 * platform-realistic — behavioral telemetry into existing
 * `content_assets`. Populates `content_metrics` and writes
 * forensic rows into `content_events` so the Adaptive Learning
 * Engine has live behavioral patterns to mine during development.
 *
 * NOT test data tooling. NOT random noise.
 * Simulates real platform behavioral signatures:
 *   TikTok    — high replay,    lower completion, fast escalation
 *   YouTube   — higher completion, lower replay,  slower pacing
 *   Instagram — high saves,     emotional ambiguity preference
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Radio, Activity, Loader2, Zap, Layers, Target, Waves,
} from "lucide-react";
import { CONTENT_ASSETS_CHANGED_EVENT, emitContentAssetsChanged } from "@/lib/contentTelemetry";

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const VOLUME_STEPS = [10, 50, 100, 500, 1000] as const;
type Platform = "tiktok" | "youtube" | "instagram";
type BehavioralBias =
  | "replay_heavy" | "save_heavy" | "comment_heavy" | "share_heavy" | "completion_heavy";

const PLATFORMS: { key: Platform; label: string }[] = [
  { key: "tiktok",    label: "TikTok" },
  { key: "youtube",   label: "YouTube" },
  { key: "instagram", label: "Instagram" },
];

const BEHAVIOR_OPTIONS: { key: BehavioralBias; label: string }[] = [
  { key: "replay_heavy",     label: "Replay Heavy" },
  { key: "save_heavy",       label: "Save Heavy" },
  { key: "comment_heavy",    label: "Comment Heavy" },
  { key: "share_heavy",      label: "Share Heavy" },
  { key: "completion_heavy", label: "Completion Heavy" },
];

const ARCHETYPE_OPTIONS = [
  "gaslighting", "shame_attack", "future_faker", "withdrawal",
  "passive_aggression", "guilt_trip", "blame_shift", "love_bombing",
];

const TICKERS = [
  "Injecting replay telemetry…",
  "Platform drift detected…",
  "Escalation bias emerging…",
  "Hook retention increasing…",
  "Save velocity climbing…",
  "Signal layer updated…",
  "Pacing tolerance recalibrating…",
  "Behavioral signature consolidating…",
];

// ─────────────────────────────────────────────────────────────
// Platform behavioral signatures
// ─────────────────────────────────────────────────────────────
interface Signature {
  impressionsRange: [number, number];
  viewRatio: [number, number];      // views / impressions
  completion: [number, number];     // %
  saveRate: [number, number];       // saves / views
  shareRate: [number, number];      // shares / impressions
  commentRate: [number, number];    // comments / views
  exportBase: [number, number];
  watchSecPerView: [number, number];
}

const SIGNATURES: Record<Platform, Signature> = {
  tiktok: {
    impressionsRange: [4000, 18000],
    viewRatio:        [0.65, 0.92],
    completion:       [28, 55],
    saveRate:         [0.02, 0.06],
    shareRate:        [0.012, 0.035],
    commentRate:      [0.008, 0.022],
    exportBase:       [3, 14],
    watchSecPerView:  [4, 9],
  },
  youtube: {
    impressionsRange: [1500, 8000],
    viewRatio:        [0.18, 0.42],
    completion:       [55, 88],
    saveRate:         [0.015, 0.04],
    shareRate:        [0.004, 0.012],
    commentRate:      [0.012, 0.03],
    exportBase:       [1, 6],
    watchSecPerView:  [22, 60],
  },
  instagram: {
    impressionsRange: [2500, 11000],
    viewRatio:        [0.42, 0.7],
    completion:       [38, 68],
    saveRate:         [0.05, 0.11],
    shareRate:        [0.01, 0.028],
    commentRate:      [0.006, 0.018],
    exportBase:       [2, 9],
    watchSecPerView:  [6, 14],
  },
};

// Deterministic-ish RNG (pure JS Math.random — fine for synthetic seeding)
const rand = (min: number, max: number) => Math.random() * (max - min) + min;
const randInt = (min: number, max: number) => Math.floor(rand(min, max + 1));
const pick = <T,>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];

interface ComputedMetrics {
  impressions: number;
  views: number;
  watch_time: number;
  completion_rate: number;
  saves: number;
  shares: number;
  comments: number;
  likes: number;
  copied_responses: number;
  exports: number;
}

function generateMetrics(
  platform: Platform,
  behaviorBias: BehavioralBias[],
  archetypeMatch: boolean,
): ComputedMetrics {
  const sig = SIGNATURES[platform];

  // Archetype lift: assets aligned with biased archetypes get a 1.15-1.4x lift
  const archMul = archetypeMatch ? rand(1.15, 1.4) : 1;

  const impressions = Math.round(rand(...sig.impressionsRange) * archMul);
  let views = Math.round(impressions * rand(...sig.viewRatio));
  let completion = rand(...sig.completion);
  let saves = Math.round(views * rand(...sig.saveRate));
  let shares = Math.round(impressions * rand(...sig.shareRate));
  let comments = Math.round(views * rand(...sig.commentRate));
  let exports = randInt(...sig.exportBase);

  const apply = (mul: number, fn: () => void) => fn();
  if (behaviorBias.includes("replay_heavy")) {
    views = Math.min(impressions, Math.round(views * rand(1.25, 1.6)));
  }
  if (behaviorBias.includes("save_heavy")) {
    saves = Math.round(saves * rand(1.6, 2.4));
  }
  if (behaviorBias.includes("comment_heavy")) {
    comments = Math.round(comments * rand(1.7, 2.6));
  }
  if (behaviorBias.includes("share_heavy")) {
    shares = Math.round(shares * rand(1.8, 2.8));
  }
  if (behaviorBias.includes("completion_heavy")) {
    completion = Math.min(96, completion * rand(1.2, 1.45));
  }

  const watch_time = Math.round(views * rand(...sig.watchSecPerView));
  const likes = Math.round(views * rand(0.04, 0.12));
  const copied_responses = randInt(0, Math.max(1, Math.round(views * 0.005)));

  return {
    impressions,
    views,
    watch_time,
    completion_rate: Math.round(completion * 10) / 10,
    saves: Math.max(0, saves),
    shares: Math.max(0, shares),
    comments: Math.max(0, comments),
    likes: Math.max(0, likes),
    copied_responses,
    exports: Math.max(0, exports),
  };
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
const SignalSeeder = () => {
  const [volumeIdx, setVolumeIdx] = useState<number>(2); // default 100
  const [platforms, setPlatforms] = useState<Set<Platform>>(
    new Set(["tiktok", "youtube", "instagram"]),
  );
  const [behaviorBias, setBehaviorBias] = useState<Set<BehavioralBias>>(new Set());
  const [archetypeBias, setArchetypeBias] = useState<Set<string>>(new Set());

  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [stats, setStats] = useState<{ assets: number; events: number } | null>(null);
  const tickRef = useRef(0);

  const volume = VOLUME_STEPS[volumeIdx];

  // live ticker pulse
  useEffect(() => {
    const id = window.setInterval(() => {
      tickRef.current += 1;
      setTickerIdx((i) => (i + 1) % TICKERS.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, []);

  const toggle = <T,>(set: Set<T>, val: T): Set<T> => {
    const n = new Set(set);
    n.has(val) ? n.delete(val) : n.add(val);
    return n;
  };

  const pushLog = useCallback((line: string) => {
    setLogs((prev) => [`${new Date().toLocaleTimeString()}  ${line}`, ...prev].slice(0, 80));
  }, []);

  const inject = useCallback(async () => {
    if (running) return;
    if (platforms.size === 0) {
      pushLog("⚠ No platform bias selected. Aborting.");
      return;
    }

    setRunning(true);
    setStats(null);
    setLogs([]);
    pushLog(`▌ Initializing behavioral seeding · volume=${volume}`);
    pushLog(`▌ Platform bias: ${[...platforms].map((p) => p.toUpperCase()).join(" · ")}`);
    if (behaviorBias.size > 0) {
      pushLog(`▌ Behavioral bias: ${[...behaviorBias].join(", ")}`);
    }
    if (archetypeBias.size > 0) {
      pushLog(`▌ Archetype bias: ${[...archetypeBias].join(", ")}`);
    }

    try {
      // 1) sample assets
      const { data: assets, error: aErr } = await (supabase as any)
        .from("content_assets")
        .select("id, archetype, platform, format, hook")
        .order("created_at", { ascending: false })
        .limit(2000);
      if (aErr) throw aErr;
      if (!assets || assets.length === 0) {
        pushLog("⚠ No content_assets indexed. Generate bundles first.");
        setRunning(false);
        return;
      }
      pushLog(`✓ Indexed ${assets.length} candidate assets`);

      // shuffle + slice
      const pool = [...assets].sort(() => Math.random() - 0.5).slice(0, volume);
      pushLog(`▌ Sampled ${pool.length} assets for behavioral injection`);

      const platformList = [...platforms];
      const behaviorList = [...behaviorBias];
      const archSet = archetypeBias;

      let assetCount = 0;
      let eventCount = 0;

      // Process in tactical batches to avoid blocking UI
      const BATCH = 25;
      for (let i = 0; i < pool.length; i += BATCH) {
        const slice = pool.slice(i, i + BATCH);

        await Promise.all(slice.map(async (asset: any) => {
          const platform = pick(platformList);
          const archetypeMatch =
            archSet.size > 0 &&
            archSet.has(String(asset.archetype || "").toLowerCase());

          const m = generateMetrics(platform, behaviorList, archetypeMatch);
          const engagement_score =
            Math.min(100, Math.round(
              (m.shares * 3 + m.saves * 2 + m.copied_responses * 4 + m.exports * 2 + m.completion_rate) / 1.5
            ));

          // Upsert metrics row (admin RLS allows full access)
          await (supabase as any).from("content_metrics").upsert(
            {
              content_asset_id: asset.id,
              ...m,
              engagement_score,
              ctr: m.views > 0 ? Math.round((m.views / Math.max(1, m.impressions)) * 1000) / 10 : 0,
              avg_watch_time: m.views > 0 ? Math.round((m.watch_time / m.views) * 10) / 10 : 0,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "content_asset_id" }
          );
          assetCount += 1;

          // Synthetic forensic events (4–9 per asset)
          const eventTypes: { type: string; weight: number }[] = [
            { type: "view",    weight: Math.min(8, Math.ceil(m.views / 200)) },
            { type: "replay",  weight: behaviorList.includes("replay_heavy") ? 3 : 1 },
            { type: "save",    weight: behaviorList.includes("save_heavy") ? 3 : 1 },
            { type: "share",   weight: behaviorList.includes("share_heavy") ? 3 : 1 },
            { type: "comment", weight: behaviorList.includes("comment_heavy") ? 3 : 1 },
            { type: "export",  weight: m.exports > 5 ? 2 : 1 },
          ];
          const events = eventTypes
            .flatMap(({ type, weight }) =>
              Array.from({ length: weight }, () => ({
                content_asset_id: asset.id,
                event_type: type,
                metadata: {
                  source: "signal_seeder",
                  platform,
                  behavioral_bias: behaviorList,
                  archetype_match: archetypeMatch,
                },
                timestamp_ms: randInt(500, 45000),
              }))
            );
          if (events.length > 0) {
            await (supabase as any).from("content_events").insert(events);
            eventCount += events.length;
          }
        }));

        pushLog(`▌ Batch ${Math.floor(i / BATCH) + 1} · ${Math.min(i + BATCH, pool.length)}/${pool.length} assets seeded`);
      }

      // Log a top-level seeding event for audit
      await (supabase as any).from("content_events").insert({
        content_asset_id: null,
        event_type: "signals_injected",
        metadata: {
          volume,
          assetCount,
          eventCount,
          platforms: platformList,
          behavioral_bias: behaviorList,
          archetype_bias: [...archSet],
        },
      });

      setStats({ assets: assetCount, events: eventCount });
      pushLog(`✓ Behavioral signal layer updated · ${assetCount} assets · ${eventCount} events`);
      pushLog(`✓ Adaptive Learning Engine downstream refresh dispatched`);

      // Notify all downstream intelligence consumers
      emitContentAssetsChanged({ source: "signal_seeder", assetCount, eventCount });
    } catch (e: any) {
      console.error("[SignalSeeder] failed", e);
      pushLog(`✗ Injection failure: ${e?.message ?? "unknown"}`);
    } finally {
      setRunning(false);
    }
  }, [running, volume, platforms, behaviorBias, archetypeBias, pushLog]);

  // Projection — what this run will roughly produce
  const projection = useMemo(() => {
    const platformCount = Math.max(1, platforms.size);
    const eventsPerAsset = 8 +
      (behaviorBias.has("replay_heavy") ? 2 : 0) +
      (behaviorBias.has("save_heavy") ? 2 : 0) +
      (behaviorBias.has("share_heavy") ? 2 : 0) +
      (behaviorBias.has("comment_heavy") ? 2 : 0);
    return {
      assets: volume,
      events: volume * eventsPerAsset,
      platformCount,
    };
  }, [volume, behaviorBias, platforms]);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm relative overflow-hidden">
      {/* signal scan line */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan/60 to-transparent animate-pulse" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="w-4 h-4 text-cyan animate-pulse" />
            Signal Seeder
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
              Behavioral Signal Injection Console
            </span>
          </CardTitle>
          <Badge variant="outline" className="border-amber-400/40 text-amber-300 font-mono text-[10px]">
            <Activity className="w-3 h-3 mr-1" />
            {running ? "INJECTING" : "ARMED"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* ── Controls grid ──────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Seed Volume */}
          <div className="rounded-md border border-glass-border bg-charcoal-deep/60 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
                <Zap className="w-3.5 h-3.5 text-cyan" />
                Seed Volume
              </div>
              <span className="text-cyan font-mono text-sm">{volume}</span>
            </div>
            <Slider
              min={0}
              max={VOLUME_STEPS.length - 1}
              step={1}
              value={[volumeIdx]}
              onValueChange={(v) => setVolumeIdx(v[0])}
              disabled={running}
            />
            <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
              {VOLUME_STEPS.map((s) => (
                <span key={s} className={s === volume ? "text-cyan" : ""}>{s}</span>
              ))}
            </div>
          </div>

          {/* Platform Bias */}
          <div className="rounded-md border border-glass-border bg-charcoal-deep/60 p-4 space-y-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
              <Layers className="w-3.5 h-3.5 text-cyan" />
              Platform Bias
            </div>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => {
                const on = platforms.has(p.key);
                return (
                  <button
                    key={p.key}
                    type="button"
                    disabled={running}
                    onClick={() => setPlatforms((s) => toggle(s, p.key))}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-mono rounded border transition-colors ${
                      on
                        ? "border-cyan/60 bg-cyan/10 text-cyan"
                        : "border-glass-border bg-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Behavioral Bias */}
          <div className="rounded-md border border-glass-border bg-charcoal-deep/60 p-4 space-y-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
              <Waves className="w-3.5 h-3.5 text-cyan" />
              Behavioral Bias
            </div>
            <div className="flex flex-wrap gap-2">
              {BEHAVIOR_OPTIONS.map((b) => {
                const on = behaviorBias.has(b.key);
                return (
                  <button
                    key={b.key}
                    type="button"
                    disabled={running}
                    onClick={() => setBehaviorBias((s) => toggle(s, b.key))}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-mono rounded border transition-colors ${
                      on
                        ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
                        : "border-glass-border bg-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {b.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Archetype Bias */}
          <div className="rounded-md border border-glass-border bg-charcoal-deep/60 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
                <Target className="w-3.5 h-3.5 text-cyan" />
                Archetype Bias
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">Optional</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {ARCHETYPE_OPTIONS.map((a) => {
                const on = archetypeBias.has(a);
                return (
                  <button
                    key={a}
                    type="button"
                    disabled={running}
                    onClick={() => setArchetypeBias((s) => toggle(s, a))}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-mono rounded border transition-colors ${
                      on
                        ? "border-cyan/50 bg-cyan/10 text-cyan"
                        : "border-glass-border bg-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {a.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Projection + Action ────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-glass-border bg-charcoal-deep/40 px-4 py-3">
          <div className="flex flex-wrap items-center gap-5 text-[11px] uppercase tracking-wider font-mono">
            <div>
              <span className="text-muted-foreground">Projected assets · </span>
              <span className="text-cyan">{projection.assets}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Projected events · </span>
              <span className="text-cyan">{projection.events.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Platform vectors · </span>
              <span className="text-cyan">{projection.platformCount}</span>
            </div>
          </div>
          <Button
            onClick={inject}
            disabled={running || platforms.size === 0}
            className="bg-cyan/15 hover:bg-cyan/25 text-cyan border border-cyan/40 font-mono text-xs uppercase tracking-wider"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Injecting Signals
              </>
            ) : (
              <>
                <Radio className="w-4 h-4 mr-2" />
                Inject Behavioral Signals
              </>
            )}
          </Button>
        </div>

        {/* ── Live ticker + log ──────────────────────────────── */}
        <div className="rounded-md border border-glass-border bg-black/50 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-glass-border bg-charcoal-deep/60">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-cyan font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse" />
              {running ? TICKERS[tickerIdx] : "Pipeline idle · awaiting injection"}
            </div>
            {stats && (
              <div className="text-[10px] font-mono text-muted-foreground">
                LAST RUN · {stats.assets} assets · {stats.events} events
              </div>
            )}
          </div>
          <ScrollArea className="h-44">
            <div className="p-3 font-mono text-[11px] leading-relaxed text-muted-foreground space-y-0.5">
              {logs.length === 0 ? (
                <div className="text-muted-foreground/50">
                  &gt; signal seeder ready. configure bias vectors and inject.
                </div>
              ) : (
                logs.map((line, i) => (
                  <div key={i} className={i === 0 ? "text-cyan" : ""}>
                    &gt; {line}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
};

export default SignalSeeder;

// Re-export for downstream consumers that want to listen
export { CONTENT_ASSETS_CHANGED_EVENT };
